import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { AppDispatch } from "@/app/store";
import { RootState } from "@/app/rootReducer";
import {
  fetchCampaignDetailsMetadataAction,
  fetchAggregatedProspectsAction,
  createCampaignAction,
} from "@/modules/stochastic/features/CampaignManagement/slices/createCampaignSlice";
import { FetchAggregatedProspectsRequest } from "@/api/fetchAggregatedProspects/types";
import { CampaignProduct } from "@/modules/stochastic/features/CampaignManagement/api/fetchCampaignProducts/types";
import { CreateCampaignRequest } from "@/modules/stochastic/features/CampaignManagement/api/createCampaign/types";
import { useNotification } from "@/context/NotificationContext";
import { FilePickerSelectedFile } from "@/modules/hub/features/FileManagement/components/FilePickerDialog/FilePickerDialog";

import { fetchCampaignProducts } from "@/modules/stochastic/features/CampaignManagement/api/fetchCampaignProducts/fetchCampaignProductsApi";

/**
 * Local ZipCode interface for this UI
 */
interface ZipCode {
  code: string;
  avgSale: number;
  availableProspects: number;
  selectedProspects: string;
  filteredProspects: number;
}

/**
 * FilterCriteria used in the form
 */
interface FilterCriteria {
  audience: string;
  prospectAge: { min: string; max: string };
  estimatedIncome: string;
  homeAge: string;
  excludeClubMembers: boolean;
  excludeLTV: boolean;
  excludeInstallCustomers: boolean;
  addressType: string;
}

/**
 * The entire FormData structure
 */
interface FormData {
  campaignName: string;
  campaignProductId: string;
  billingType: string;
  description: string;
  phoneNumber: string;
  startDate: string;
  endDate: string;
  licenseNumber: string;
  permitNumber: string;
  mailingFrequency: string;
  selectedMailingWeeks: number[];
  locations: number[];
  filterCriteria: FilterCriteria;
  zipCodes: ZipCode[];
  tags: string;
  creativeAssetFrontUuid: string | null;
  creativeAssetBackUuid: string | null;
}

export function useCreateEditCampaign() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const {
    campaignDetailsMetadata,
    loadingCampaignDetailsMetadata,
    errorCampaignDetailsMetadata,
    aggregatedProspects,
    loadingAggregatedProspects,
    errorAggregatedProspects,
    loadingCreate,
  } = useSelector((state: RootState) => state.createCampaign);

  const [campaignProducts, setCampaignProducts] = useState<CampaignProduct[]>(
    [],
  );

  const [loadingCampaignProducts, setLoadingCampaignProducts] =
    useState<boolean>(false);
  const [errorCampaignProducts, setErrorCampaignProducts] = useState<
    string | null
  >(null);

  const [formData, setFormData] = useState<FormData>({
    campaignName: "",
    campaignProductId: "",
    billingType: "metered",
    description: "",
    phoneNumber: "",
    startDate: "",
    endDate: "",
    licenseNumber: "",
    permitNumber: "",
    mailingFrequency: "",
    selectedMailingWeeks: [],
    locations: [],
    filterCriteria: {
      audience: "include_prospects_only",
      addressType: "include_residential_only",
      prospectAge: { min: "40", max: "90" },
      estimatedIncome: "",
      homeAge: "5",
      excludeClubMembers: false,
      excludeLTV: false,
      excludeInstallCustomers: false,
    },
    zipCodes: [],
    tags: "",
    creativeAssetFrontUuid: null,
    creativeAssetBackUuid: null,
  });

  const [creativeAssetFrontFileName, setCreativeAssetFrontFileName] =
    useState<string>("");
  const [creativeAssetFrontDisplayUrl, setCreativeAssetFrontDisplayUrl] =
    useState<string>("");
  const [isCreativeAssetFrontPickerOpen, setIsCreativeAssetFrontPickerOpen] =
    useState<boolean>(false);
  const [creativeAssetBackFileName, setCreativeAssetBackFileName] =
    useState<string>("");
  const [creativeAssetBackDisplayUrl, setCreativeAssetBackDisplayUrl] =
    useState<string>("");
  const [isCreativeAssetBackPickerOpen, setIsCreativeAssetBackPickerOpen] =
    useState<boolean>(false);

  const openCreativeAssetFrontPicker = () =>
    setIsCreativeAssetFrontPickerOpen(true);
  const closeCreativeAssetFrontPicker = () =>
    setIsCreativeAssetFrontPickerOpen(false);
  const openCreativeAssetBackPicker = () =>
    setIsCreativeAssetBackPickerOpen(true);
  const closeCreativeAssetBackPicker = () =>
    setIsCreativeAssetBackPickerOpen(false);

  const handleCreativeAssetFrontSelected = (
    files: FilePickerSelectedFile[],
  ) => {
    if (files.length === 0) return;
    const file = files[0];
    setCreativeAssetFrontDisplayUrl(file.publicUrl);
    setCreativeAssetFrontFileName(file.originalFilename || file.name);
    setFormData((prev) => ({ ...prev, creativeAssetFrontUuid: file.fileUuid }));
    setIsCreativeAssetFrontPickerOpen(false);
  };

  const handleRemoveCreativeAssetFront = () => {
    setCreativeAssetFrontDisplayUrl("");
    setCreativeAssetFrontFileName("");
    setFormData((prev) => ({ ...prev, creativeAssetFrontUuid: null }));
  };

  const handleCreativeAssetBackSelected = (files: FilePickerSelectedFile[]) => {
    if (files.length === 0) return;
    const file = files[0];
    setCreativeAssetBackDisplayUrl(file.publicUrl);
    setCreativeAssetBackFileName(file.originalFilename || file.name);
    setFormData((prev) => ({ ...prev, creativeAssetBackUuid: file.fileUuid }));
    setIsCreativeAssetBackPickerOpen(false);
  };

  const handleRemoveCreativeAssetBack = () => {
    setCreativeAssetBackDisplayUrl("");
    setCreativeAssetBackFileName("");
    setFormData((prev) => ({ ...prev, creativeAssetBackUuid: null }));
  };

  const [isFiltering, setIsFiltering] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filtersDirty, setFiltersDirty] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [showLowProspectWarningModal, setShowLowProspectWarningModal] =
    useState(false);
  const [lowProspectZipCodes, setLowProspectZipCodes] = useState<
    Array<{
      code: string;
      selectedProspects: number;
    }>
  >([]);

  useEffect(() => {
    dispatch(fetchCampaignDetailsMetadataAction());

    // Fetch campaign products
    const fetchProducts = async () => {
      setLoadingCampaignProducts(true);
      try {
        const response = await fetchCampaignProducts({ pageSize: 100 });
        setCampaignProducts(response.data.campaignProducts);
      } catch (error) {
        console.error("Error fetching campaign products:", error);
        setErrorCampaignProducts("Failed to load campaign products");
      } finally {
        setLoadingCampaignProducts(false);
      }
    };

    fetchProducts();
  }, [dispatch]);

  useEffect(() => {
    const updatedZipCodes: ZipCode[] = aggregatedProspects.map((ap) => {
      return {
        code: ap.postalCode,
        avgSale: ap.avgSales,
        availableProspects: ap.households,
        selectedProspects: "",
        filteredProspects: ap.households,
      };
    });

    setFormData((prev) => ({
      ...prev,
      zipCodes: updatedZipCodes,
    }));
  }, [aggregatedProspects]);

  /**
   * Whenever the audience becomes "include_prospects_only",
   * clear out the existing customer restriction criteria.
   */
  useEffect(() => {
    if (formData.filterCriteria.audience === "include_prospects_only") {
      setFormData((prev) => ({
        ...prev,
        filterCriteria: {
          ...prev.filterCriteria,
          excludeClubMembers: false,
          excludeLTV: false,
          excludeInstallCustomers: false,
        },
      }));
    }
  }, [formData.filterCriteria.audience]);

  /**
   * Show/hide the "Existing Customer Restriction Criteria"
   * based on whether the audience is "include_prospects_only".
   */
  const showExclusionFilters =
    formData.filterCriteria.audience &&
    formData.filterCriteria.audience !== "include_prospects_only";

  const handleInputChange = (field: keyof FormData, value: string) => {
    // If user updates mailingFrequency, ensure selectedMailingWeeks doesn't exceed that new value
    if (field === "mailingFrequency") {
      const newDuration = parseInt(value);
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        selectedMailingWeeks: prev.selectedMailingWeeks.filter(
          (week) => week < newDuration,
        ),
      }));
    } else if (field === "campaignProductId") {
      setFormData((prev) => ({
        ...prev,
        campaignProductId: value,
      }));
    } else if (field === "locations") {
      const locationArray = value
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id));
      setFormData((prev) => ({
        ...prev,
        [field]: locationArray,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (field === "tags" || field === "locations") {
      setFiltersDirty(true);
    }
  };

  const handleFilterChange = (
    field: string,
    value: string | boolean | { min: string; max: string },
  ) => {
    setFormData((prev) => ({
      ...prev,
      filterCriteria: {
        ...prev.filterCriteria,
        [field]: value,
      },
    }));
    setFiltersDirty(true);
  };

  const applyFilters = async () => {
    setIsFiltering(true);
    setFiltersDirty(false);

    const requestParams: FetchAggregatedProspectsRequest = {
      customerInclusionRule: formData.filterCriteria.audience,
      addressTypeRule: formData.filterCriteria.addressType,
      prospectMinAgeRule:
        parseInt(formData.filterCriteria.prospectAge.min) || 0,
      prospectMaxAgeRule:
        parseInt(formData.filterCriteria.prospectAge.max) || 999,
      minEstimatedIncomeRule:
        formData.filterCriteria.estimatedIncome || undefined,
      minHomeAgeRule: parseInt(formData.filterCriteria.homeAge) || undefined,

      clubMembersRule: formData.filterCriteria.excludeClubMembers
        ? "exclude_club_members"
        : "",
      lifetimeValueRule: formData.filterCriteria.excludeLTV ? "5000" : "",
      installationsRule: formData.filterCriteria.excludeInstallCustomers
        ? "exclude_customer_installations"
        : "",
      tagsRule: formData.tags,
      locations: formData.locations,
    };

    try {
      await dispatch(fetchAggregatedProspectsAction(requestParams));
      setFiltersApplied(true);
    } catch (error) {
      console.error("Error applying filters:", error);
    } finally {
      setIsFiltering(false);
    }
  };

  const handleZipCodeChange = (index: number, value: string) => {
    const newZipCodes = [...formData.zipCodes];
    newZipCodes[index] = {
      ...newZipCodes[index],
      selectedProspects: value,
    };
    setFormData((prev) => ({
      ...prev,
      zipCodes: newZipCodes,
    }));
  };

  const handleSelectAllProspects = () => {
    setFormData((prev) => ({
      ...prev,
      zipCodes: prev.zipCodes.map((zip) => ({
        ...zip,
        selectedProspects: zip.filteredProspects.toString(),
      })),
    }));
  };

  const handleUnselectAllProspects = () => {
    setFormData((prev) => ({
      ...prev,
      zipCodes: prev.zipCodes.map((zip) => ({
        ...zip,
        selectedProspects: "",
      })),
    }));
  };

  const hasAnyProspectsSelected = () => {
    return formData.zipCodes.some((zip) => zip.selectedProspects !== "");
  };

  const toggleMailingWeek = (weekIndex: number) => {
    setFormData((prev) => {
      const currentWeeks = prev.selectedMailingWeeks;
      let newWeeks;

      if (currentWeeks.includes(weekIndex)) {
        // If removing an already selected week, allow removal only if more than 1 selected
        if (currentWeeks.length > 1) {
          newWeeks = currentWeeks.filter((w) => w !== weekIndex);
        } else {
          newWeeks = currentWeeks;
        }
      } else {
        newWeeks = [...currentWeeks, weekIndex].sort((a, b) => a - b);
      }

      return {
        ...prev,
        selectedMailingWeeks: newWeeks,
      };
    });
  };

  const getTotalProspects = () => {
    return formData.zipCodes.reduce((sum, zip) => {
      const selected = parseInt(zip.selectedProspects) || 0;
      return sum + selected;
    }, 0);
  };

  const getProspectsPerMailing = () => {
    const total = getTotalProspects();
    return formData.selectedMailingWeeks.length > 0
      ? Math.ceil(total / formData.selectedMailingWeeks.length)
      : total;
  };

  const hasAppliedFilters = () => {
    return formData.zipCodes.some((zip) => parseInt(zip.selectedProspects) > 0);
  };

  const getEarliestAllowedCampaignDate = () => {
    const now = DateTime.now();
    const currentWeek = now.weekNumber;
    const currentYear = now.weekYear;

    if (!campaignDetailsMetadata?.campaignWeekEvent) {
      const currentWeekStart = DateTime.fromObject({
        weekYear: currentYear,
        weekNumber: currentWeek,
        weekday: 1,
      });
      return currentWeekStart.toISODate() || undefined;
    }

    const { week, year } = campaignDetailsMetadata.campaignWeekEvent;

    if (week === currentWeek && year === currentYear) {
      const nextWeekStart = DateTime.fromObject({
        weekYear: currentYear,
        weekNumber: currentWeek + 1,
        weekday: 1,
      });
      return nextWeekStart.toISODate() || undefined;
    }

    const campaignWeekDate = DateTime.fromObject({
      weekYear: year,
      weekNumber: week,
      weekday: 1,
    });

    const currentWeekStart = DateTime.fromObject({
      weekYear: currentYear,
      weekNumber: currentWeek,
      weekday: 1,
    });

    if (campaignWeekDate < currentWeekStart) {
      return currentWeekStart.toISODate() || undefined;
    }

    const minAllowedWeekStart = DateTime.fromObject({
      weekYear: year,
      weekNumber: week + 1,
      weekday: 1,
    });

    return minAllowedWeekStart.toISODate() || undefined;
  };

  const checkForLowProspectCounts = () => {
    return formData.zipCodes
      .filter((zip) => {
        const selectedCount = parseInt(zip.selectedProspects) || 0;
        return selectedCount > 0 && selectedCount < 150;
      })
      .map((zip) => ({
        code: zip.code,
        selectedProspects: parseInt(zip.selectedProspects) || 0,
      }));
  };

  const handleCreateCampaign = async () => {
    try {
      // Build the request
      const requestData: CreateCampaignRequest = {
        campaignName: formData.campaignName,
        campaignProduct: formData.campaignProductId
          ? parseInt(formData.campaignProductId)
          : null,
        billingType: formData.billingType,
        description: formData.description,
        phoneNumber: formData.phoneNumber,
        startDate: formData.startDate,
        endDate: formData.endDate,
        licenseNumber: formData.licenseNumber.trim() || null,
        permitNumber: formData.permitNumber.trim() || null,
        mailingFrequency: parseInt(formData.mailingFrequency) || 0,
        selectedMailingWeeks: formData.selectedMailingWeeks,
        locations: formData.locations,
        filterCriteria: formData.filterCriteria,
        zipCodes: formData.zipCodes,
        tags: formData.tags,
        creativeAssetFrontUuid: formData.creativeAssetFrontUuid,
        creativeAssetBackUuid: formData.creativeAssetBackUuid,
      };

      dispatch(
        createCampaignAction(requestData, () => {
          showNotification(
            "Successfully created campaign!",
            "Your campaign has been queued for creation",
            "success",
          );
          // Navigate away
          navigate("/lead-generation/campaigns");
        }),
      );
    } catch (err) {
      console.error("Failed to create campaign:", err);
    }
  };

  const handleLowProspectWarningModalConfirm = () => {
    setShowLowProspectWarningModal(false);
    handleCreateCampaign();
  };

  const handleLowProspectWarningModalClose = () => {
    setShowLowProspectWarningModal(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setStartDateError(null);
    setEndDateError(null);
    setDateError(null);

    let hasErrors = false;

    if (!formData.startDate.trim()) {
      setStartDateError("Start date is required.");
      hasErrors = true;
    }

    if (!formData.endDate.trim()) {
      setEndDateError("End date is required.");
      hasErrors = true;
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (start > end) {
        setDateError("Start date cannot be later than end date.");
        hasErrors = true;
      }
    }

    if (hasErrors) {
      return;
    }

    // Check for low prospect counts before creating a campaign
    const lowProspectZips = checkForLowProspectCounts();
    if (lowProspectZips.length > 0) {
      setLowProspectZipCodes(lowProspectZips);
      setShowLowProspectWarningModal(true);
      return;
    }

    // If all zip codes have 150+ prospects, create a campaign directly
    handleCreateCampaign();
  };

  return {
    formData,
    setFormData,
    campaignDetailsMetadata,
    loadingCampaignDetailsMetadata,
    loadingCreate,
    errorCampaignDetailsMetadata,
    aggregatedProspects,
    loadingAggregatedProspects,
    errorAggregatedProspects,
    showExclusionFilters,
    isFiltering,
    filtersDirty,
    setFiltersDirty,
    filtersApplied,
    dateError,
    startDateError,
    endDateError,
    showLowProspectWarningModal,
    lowProspectZipCodes,
    handleInputChange,
    handleFilterChange,
    handleZipCodeChange,
    handleSelectAllProspects,
    handleUnselectAllProspects,
    hasAnyProspectsSelected,
    applyFilters,
    handleSubmit,
    toggleMailingWeek,
    getTotalProspects,
    getProspectsPerMailing,
    hasAppliedFilters,
    campaignProducts,
    loadingCampaignProducts,
    errorCampaignProducts,
    getEarliestAllowedCampaignDate,
    handleLowProspectWarningModalConfirm,
    handleLowProspectWarningModalClose,
    creativeAssetFrontFileName,
    creativeAssetFrontDisplayUrl,
    isCreativeAssetFrontPickerOpen,
    openCreativeAssetFrontPicker,
    closeCreativeAssetFrontPicker,
    handleCreativeAssetFrontSelected,
    handleRemoveCreativeAssetFront,
    creativeAssetBackFileName,
    creativeAssetBackDisplayUrl,
    isCreativeAssetBackPickerOpen,
    openCreativeAssetBackPicker,
    closeCreativeAssetBackPicker,
    handleCreativeAssetBackSelected,
    handleRemoveCreativeAssetBack,
  };
}
